import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import DrillDownDrawer, { cellDate, cellMono, cellBadge } from "../components/DrillDownDrawer";
import ProducerReviewInlineDrawer from "../components/ProducerReviewInlineDrawer";

const RISK_TONE = { high: "text-danger", elevated: "text-ink", standard: "text-ink2" };
const RISK_BAR = { high: "bg-danger", elevated: "bg-ink", standard: "bg-leaf" };
const RISK_BADGE = { high: "bg-danger text-white", elevated: "bg-ink text-white", standard: "bg-muted text-ink" };
const STATUS_TONE = { verified: "text-leaf", review: "text-danger" };

const KYC_COLUMNS = [
  { key: "submitted_at", label: "Submitted", render: (r) => cellDate(r.submitted_at) },
  { key: "company_name", label: "Company" },
  { key: "country", label: "Country", render: (r) => cellMono(r.country) },
  { key: "user_name", label: "Client" },
  { key: "user_email", label: "Email" },
  { key: "signatory_name", label: "Signatory" },
  { key: "signatory_position", label: "Position" },
  { key: "risk_rating", label: "Risk", render: (r) => cellBadge(r.risk_rating,
      r.risk_rating === "high" ? "danger" : r.risk_rating === "elevated" ? "ink" : "muted") },
  { key: "status", label: "Status", render: (r) => cellBadge(r.status,
      r.status === "verified" ? "leaf" : r.status === "review" ? "danger" : "muted") },
  { key: "next_review_at", label: "Next review", render: (r) => cellDate(r.next_review_at) },
];

export default function Compliance() {
  const { t } = useTranslation();
  const tr = (k, o) => t(`compliance_page.${k}`, o);
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drill, setDrill] = useState(null);
  const [producerQueue, setProducerQueue] = useState({ items: [], counts: {} });
  const [prodActive, setProdActive] = useState(null);

  const load = () => api.get("/admin/compliance/stats").then((r) => setS(r.data));
  const loadProducers = () => api.get("/admin/producers/queue")
    .then((r) => setProducerQueue({ items: r.data.items || [], counts: r.data.counts || {} }))
    .catch(() => {});
  useEffect(() => { load(); loadProducers(); }, []);

  const openKyc = (title, subtitle, params, filename) => setDrill({
    title, subtitle,
    endpoint: "/admin/compliance/kyc-list",
    params,
    csvFilename: filename,
    columns: KYC_COLUMNS,
  });

  const rescreen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/kyc/rescreen");
      toast.success(`Rescreen: ${data.rerated} re-rated of ${data.due} due (${data.checked} checked)`);
      load();
    } finally { setBusy(false); }
  };

  if (!s) return <Layout><div className="max-w-7xl mx-auto p-10 text-ink2">Loading…</div></Layout>;

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-10 flex items-end justify-between">
        <div>
          <div className="label-tag mb-4">{tr("tag")}</div>
          <h1 className="font-serif text-5xl tracking-tight">{tr("title")}</h1>
          <p className="text-[11px] font-mono text-ink2 mt-3 italic">{tr("tip", "Tip — click any tile, risk bar, or country chip to open the underlying list.")}</p>
        </div>
        <button onClick={rescreen} disabled={busy} className="btn-primary !py-2 !px-5 !text-xs disabled:opacity-50" data-testid="rescreen-btn">
          {busy ? tr("running", "Running…") : `${tr("run_rescreen", "Run rescreen")} →`}
        </button>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-5 gap-px bg-line border border-line">
        <Tile k={tr("total")} v={s.total} onClick={() => openKyc("All KYB entities", "No filter applied", {}, "kyc_all.csv")} />
        <Tile k={tr("verified")} v={s.by_status.verified || 0} tone="leaf"
          onClick={() => openKyc("Verified KYB entities", "status = verified", { status: "verified" }, "kyc_verified.csv")} />
        <Tile k={tr("in_review")} v={s.by_status.review || 0} tone="danger"
          onClick={() => openKyc("KYB in review", "status = review (manual approval needed)", { status: "review" }, "kyc_review.csv")} />
        <Tile k={tr("due_rescreen")} v={s.due_review} tone={s.due_review ? "danger" : ""}
          onClick={() => openKyc("KYB due for rescreen", "next_review_at ≤ today", { due_review: true }, "kyc_due_review.csv")} />
        <Tile k={tr("pep_flagged")} v={s.pep_flagged} tone={s.pep_flagged ? "danger" : ""}
          onClick={() => openKyc("PEP-flagged entities", "pep_declaration = true", { pep: true }, "kyc_pep.csv")} />
      </section>

      {/* Subject to approval — producer listings awaiting compliance */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-8" data-testid="producer-approvals-section">
        <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
          <div>
            <div className="label-tag mb-1">{tr("producer_approvals_tag")}</div>
            <h2 className="font-serif text-2xl tracking-tight">
              {tr("awaiting_review", { count: producerQueue.items.length, plural: producerQueue.items.length === 1 ? "" : "s" })}
            </h2>
            <p className="text-[11px] font-mono text-ink2 mt-1 italic">
              {tr("awaiting_note")}
            </p>
          </div>
          <Link
            to="/admin/producers"
            className="text-[11px] font-mono uppercase tracking-wider text-leaf hover:text-ink underline underline-offset-2"
            data-testid="prod-open-full-queue"
          >
            {tr("full_producer_queue")} →
          </Link>
        </div>
        {producerQueue.items.length === 0 ? (
          <div className="border border-line bg-surface p-8 text-center text-ink2 text-sm">
            {tr("none_pending")}
          </div>
        ) : (
          <div className="border border-line bg-surface overflow-x-auto" data-testid="prod-approvals-table">
            <table className="w-full text-sm">
              <thead className="bg-muted text-[11px] font-mono uppercase tracking-wider text-ink2">
                <tr>
                  <th className="text-left p-3">{tr("col_listing")}</th>
                  <th className="text-left p-3">{tr("col_producer")}</th>
                  <th className="text-right p-3">{tr("col_volume")}</th>
                  <th className="text-right p-3">{tr("col_docs")}</th>
                  <th className="text-left p-3">{tr("col_status")}</th>
                  <th className="text-left p-3">{tr("col_submitted")}</th>
                </tr>
              </thead>
              <tbody>
                {producerQueue.items.slice(0, 8).map((it) => {
                  const activeDocs = (it.documents || []).filter((d) => !d.is_deleted).length;
                  return (
                    <tr
                      key={it.id}
                      onClick={() => setProdActive(it)}
                      className="border-t border-line hover:bg-bg/40 cursor-pointer"
                      data-testid={`prod-approval-row-${it.id}`}
                    >
                      <td className="p-3">
                        <div>{it.name}</div>
                        <div className="text-[11px] font-mono text-ink2">{it.region} · {it.methodology} · v{it.vintage}</div>
                      </td>
                      <td className="p-3">
                        <div>{it.producer_name}</div>
                        <div className="text-[11px] font-mono text-ink2">{it.producer_email}</div>
                      </td>
                      <td className="p-3 text-right font-mono">
                        {Number(it.tons_available || 0).toLocaleString()}
                      </td>
                      <td className="p-3 text-right font-mono">{activeDocs}</td>
                      <td className="p-3">
                        <span className={`text-[10px] font-mono uppercase px-2 py-1 border ${
                          it.status === "needs_info" ? "bg-danger/10 text-danger border-danger/40"
                            : "bg-ink text-white border-ink"
                        }`}>
                          {it.status.replace("_", " ")}
                        </span>
                      </td>
                      <td className="p-3 text-[11px] font-mono text-ink2">
                        {new Date(it.updated_at).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 border border-line bg-surface p-6">
          <div className="label-tag mb-4">{tr("risk_distribution", "Risk distribution")}</div>
          {["standard","elevated","high"].map((r) => {
            const n = s.by_risk[r] || 0; const max = Math.max(1, ...Object.values(s.by_risk));
            return (
              <button
                type="button"
                key={r}
                className="block w-full text-left mb-3 group"
                data-testid={`risk-${r}`}
                onClick={() => openKyc(
                  `KYB · ${r.toUpperCase()} risk`,
                  `risk_rating = ${r}`,
                  { risk: r },
                  `kyc_risk_${r}.csv`,
                )}
              >
                <div className="flex justify-between text-xs font-mono uppercase tracking-wider mb-1 group-hover:text-ink">
                  <span className={RISK_TONE[r] || "text-ink2"}>{tr(`risk_${r}`, r)}</span>
                  <span className="mono-num">{n}</span>
                </div>
                <div className="h-2 bg-muted">
                  <div className={`h-2 ${RISK_BAR[r] || "bg-leaf"} group-hover:opacity-80`} style={{ width: `${(n / max) * 100}%` }} />
                </div>
              </button>
            );
          })}
          <div className="label-tag mt-8 mb-3">{tr("by_country", "By country")}</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(s.by_country).map(([c,n]) => (
              <button
                key={c}
                type="button"
                onClick={() => openKyc(`KYB · ${c}`, `country = ${c}`, { country: c }, `kyc_country_${c}.csv`)}
                className="px-2.5 py-1 border border-line font-mono text-xs hover:border-ink hover:bg-bg/40"
                data-testid={`country-${c}`}
              >
                {c} · {n}
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7 border border-line bg-surface overflow-x-auto">
          <div className="p-5 border-b border-line flex items-center justify-between">
            <span className="label-tag">{tr("recent_submissions", "Recent KYB submissions")}</span>
            <button
              type="button"
              onClick={() => openKyc("All KYB entities", "No filter applied", {}, "kyc_all.csv")}
              className="text-[10px] font-mono uppercase tracking-wider text-leaf hover:text-ink underline underline-offset-2"
              data-testid="open-full-kyc-list"
            >
              {tr("view_all", "View all")} →
            </button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-line text-left">
              {[tr("th_company", "Company"), tr("th_country", "Country"), tr("th_risk", "Risk"), tr("th_status_kyc", "Status"), tr("th_next_review", "Next review")].map((h)=>(
                <th key={h} className="px-4 py-3 text-[11px] tracking-[0.2em] uppercase text-ink2">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {s.recent.length===0 && <tr><td colSpan={5} className="p-8 text-center text-ink2">{tr("no_entries", "No entries.")}</td></tr>}
              {s.recent.map((k,i)=>{
                const rowKey = k.tax_id_number || k.company_name || `row-${i}`;
                return (
                <tr key={rowKey} className="border-b border-line/60" data-testid={`row-${i}`}>
                  <td className="px-4 py-3">{k.company_name||"—"}</td>
                  <td className="px-4 py-3 mono-num text-xs">{k.country||"—"}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 text-[10px] font-mono uppercase ${RISK_BADGE[k.risk_rating] || "bg-muted text-ink"}`}>{k.risk_rating||"—"}</span></td>
                  <td className="px-4 py-3"><span className={`text-xs font-mono ${STATUS_TONE[k.status] || "text-ink2"}`}>{(k.status||"—").toUpperCase()}</span></td>
                  <td className="px-4 py-3 mono-num text-xs text-ink2">{k.next_review_at?new Date(k.next_review_at).toLocaleDateString():"—"}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      <div className="h-16" />

      {drill && (
        <DrillDownDrawer
          open={!!drill}
          onOpenChange={(o) => { if (!o) setDrill(null); }}
          {...drill}
        />
      )}

      {prodActive && (
        <ProducerReviewInlineDrawer
          listing={prodActive}
          onClose={() => setProdActive(null)}
          onChanged={() => { setProdActive(null); loadProducers(); }}
        />
      )}
    </Layout>
  );
}

function Tile({ k, v, tone, onClick }) {
  const cls = tone === "leaf" ? "text-leaf" : tone === "danger" ? "text-danger" : "text-ink";
  const clickable = typeof onClick === "function";
  return (
    <div
      className={`bg-surface p-6 transition-colors ${clickable ? "cursor-pointer hover:bg-bg/40" : ""}`}
      data-testid={`tile-${k}`}
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="flex items-start justify-between">
        <div className="label-tag">{k}</div>
        {clickable && <span className="text-[10px] font-mono text-ink2">↗</span>}
      </div>
      <div className={`mono-num text-3xl mt-2 ${cls}`}>{v}</div>
    </div>
  );
}
